import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FarmData, FarmInfrastructures } from 'types/FarmData';
import { Button, GoogleMap } from 'ui';
import { publicPath } from 'enums/publicRoutes';
import { LatLng, centroid, haversineKm, parsePoint } from 'utils/geoHelpers';
import { useRoadDistances } from 'utils/useRoadDistances';
import { useUserLocation } from 'utils/useUserLocation';
import { ProducerPill } from 'public/pages/ProductPage/components/ProducerPill';
import { StyledFarmLocationCard } from './style';

const SINGLE_PIN_ZOOM = 11;
const MIN_ZOOM = 6;
const MAP_BOX = { width: 358, height: 302 };
const FIT_PADDING = 1.6;
const LAT_STRETCH = 1.84;

const zoomToFit = (points: LatLng[]): number => {
  if (points.length < 2) return SINGLE_PIN_ZOOM;

  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const lngSpan = Math.max(...lngs) - Math.min(...lngs);
  const latSpan = (Math.max(...lats) - Math.min(...lats)) * LAT_STRETCH;
  if (lngSpan === 0 && latSpan === 0) return SINGLE_PIN_ZOOM;

  const zoomFor = (span: number, px: number) =>
    Math.log2((px * 360) / (256 * span * FIT_PADDING));

  const zoom = Math.floor(
    Math.min(
      lngSpan ? zoomFor(lngSpan, MAP_BOX.width) : SINGLE_PIN_ZOOM,
      latSpan ? zoomFor(latSpan, MAP_BOX.height) : SINGLE_PIN_ZOOM
    )
  );

  return Math.min(SINGLE_PIN_ZOOM, Math.max(MIN_ZOOM, zoom));
};

interface FarmLocationCardProps {
  farm?: FarmData;
  infrastructures?: FarmInfrastructures[];
  producerName?: string | null;
  isEmarket: boolean;
  showFarmPill?: boolean;
}

interface MapPin {
  id: number;
  position: LatLng;
}

const FarmLocationCard = ({
  farm,
  infrastructures,
  producerName,
  isEmarket,
  showFarmPill = true,
}: FarmLocationCardProps) => {
  const intl = useIntl();
  const { location, status, requestLocation } = useUserLocation();
  const routeTo = useRoadDistances(farm?.id, location);

  const farmName = farm?.brand ?? farm?.name;
  const farmPath = isEmarket
    ? publicPath.farm(farm?.slug, farm?.id)
    : publicPath.wholesaleFarm(farm?.slug, farm?.id);

  const marketFlag = isEmarket
    ? 'farm_infrastructures_products.emarket'
    : 'farm_infrastructures_products.wholesale';

  const pins = useMemo<MapPin[]>(
    () =>
      (infrastructures ?? [])
        .filter((infra) => infra.show_on_map !== false)
        .filter((infra) => infra.pivot?.[marketFlag] !== false)
        .map((infra) => ({
          id: infra.id,
          position: parsePoint(infra.maps_api_point),
        }))
        .filter((pin): pin is MapPin => !!pin.position),
    [infrastructures, marketFlag]
  );

  const nearestPin = useMemo(() => {
    if (!pins.length) return null;
    if (!location) return pins[0];

    const kmTo = (pin: MapPin) =>
      routeTo(pin.id, pin.position)?.km ?? haversineKm(location, pin.position);

    return pins.reduce((nearest, pin) =>
      kmTo(pin) < kmTo(nearest) ? pin : nearest
    );
  }, [pins, location, routeTo]);

  const nearestRoute = nearestPin
    ? routeTo(nearestPin.id, nearestPin.position)
    : null;
  const distanceKm = nearestRoute ? Math.round(nearestRoute.km) : null;

  const destination = nearestPin
    ? `${nearestPin.position.lat},${nearestPin.position.lng}`
    : (farm?.actual_full_address ?? farm?.registered_full_address ?? '');

  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    destination
  )}`;
  const wazeUrl = nearestPin
    ? `https://waze.com/ul?ll=${encodeURIComponent(destination)}&z=10`
    : `https://waze.com/ul?navigate=yes&q=${encodeURIComponent(destination)}`;

  const showPill = showFarmPill && !!farmName;

  if (!pins.length && !showPill && !destination) return null;

  return (
    <StyledFarmLocationCard>
      {!!pins.length && (
        <div className={'farm-location-map'}>
          <GoogleMap
            features={pins}
            center={centroid(pins.map((pin) => pin.position)) ?? undefined}
            zoom={zoomToFit(pins.map((pin) => pin.position))}
          />
        </div>
      )}
      <div className={'farm-location-body'}>
        {showPill && (
          <ProducerPill
            label={
              producerName
                ? 'product_view.distributor_with_name'
                : 'product_view.producer_with_name'
            }
            name={farmName}
            to={farm?.id ? farmPath : undefined}
            icon={producerName ? 'distributor-icon' : 'location-pin-icon'}
          />
        )}

        {!!pins.length &&
          (distanceKm !== null ? (
            <p className={'farm-location-distance'}>
              {intl.formatMessage(
                { id: 'product_view.distance_from_you' },
                { distance: distanceKm }
              )}
            </p>
          ) : (
            <p className={'farm-location-hint'}>
              {status === 'denied' || status === 'unsupported'
                ? intl.formatMessage({ id: 'product_view.location_denied' })
                : intl.formatMessage({
                    id: 'product_view.enable_location_hint',
                  })}{' '}
              {status !== 'unsupported' && (
                <Button
                  type={'link'}
                  className={'farm-location-enable'}
                  label={intl.formatMessage({
                    id: 'product_view.enable_location',
                  })}
                  onClick={requestLocation}
                  disabled={status === 'requesting'}
                />
              )}
            </p>
          ))}

        {!!destination && (
          <div className={'farm-location-actions'}>
            <a href={googleUrl} target={'_blank'} rel={'noreferrer'}>
              {intl.formatMessage({ id: 'farms.google_map' })}
            </a>
            <a href={wazeUrl} target={'_blank'} rel={'noreferrer'}>
              {intl.formatMessage({ id: 'farms.waze' })}
            </a>
          </div>
        )}
      </div>
    </StyledFarmLocationCard>
  );
};

export default FarmLocationCard;
